import { useCallback, useEffect, useRef, useState } from "react";
import type { FaceDetector } from "@mediapipe/tasks-vision";
import { AdaptivePoseFilter, FaceTrackingContinuity, poseFromDetection, type HeadPose } from "../shared/tracking.ts";
import { useLocale } from "./i18n.tsx";
import { TRACKING_ASSETS } from "./trackingAssets.generated.ts";
import { downloadTrackingAssets, trackingWasmVariant, type TrackingLoadProgress } from "./trackingAssets.ts";
import { TrackingFrameNormalizer } from "./trackingFrame.ts";

export type TrackerStatus = "idle" | "requesting" | "loading" | "waiting-frame" | "tracking" | "recovering" | "face-lost" | "error";

const MIN_INFERENCE_INTERVAL_MS = 80;

function friendlyCameraError(error: unknown, isEnglish: boolean) {
  if (error instanceof DOMException && error.name === "NotAllowedError") return isEnglish
    ? "Camera access wasn't allowed. Allow it and retry, or use mouse mode."
    : "没有获得摄像头权限。你可以允许权限后重试，或使用鼠标试玩。";
  if (error instanceof DOMException && error.name === "NotFoundError") return isEnglish
    ? "No camera was found. You can still use mouse mode."
    : "没有找到可用摄像头。你仍然可以使用鼠标试玩。";
  return isEnglish
    ? "The camera couldn't start. Close other apps using it, then retry."
    : "摄像头暂时无法启动。请关闭占用摄像头的其他应用后重试。";
}

async function createDetector(
  signal: AbortSignal,
  onProgress: (progress: TrackingLoadProgress) => void,
) {
  const { FaceDetector, FilesetResolver } = await import("@mediapipe/tasks-vision");
  const simdSupported = await FilesetResolver.isSimdSupported(false);
  const variant = trackingWasmVariant(navigator.userAgent, simdSupported);
  const wasm = TRACKING_ASSETS.wasm[variant];
  const retained = await downloadTrackingAssets([
    { id: "loader", ...wasm.loader },
    { id: "wasm", ...wasm.binary },
    { id: "model", ...TRACKING_ASSETS.model, retain: true },
  ], signal, onProgress);
  const modelAssetBuffer = retained.get("model");
  if (!modelAssetBuffer) throw new Error("Tracking model was not retained after download");
  const totalBytes = wasm.loader.size + wasm.binary.size + TRACKING_ASSETS.model.size;
  onProgress({ phase: "initializing", loadedBytes: totalBytes, totalBytes, percent: 100 });

  return FaceDetector.createFromOptions({
    wasmLoaderPath: wasm.loader.path,
    wasmBinaryPath: wasm.binary.path,
  }, {
    baseOptions: { modelAssetBuffer, delegate: "CPU" },
    runningMode: "VIDEO",
    minDetectionConfidence: 0.25,
    minSuppressionThreshold: 0.3,
  });
}

export function useHeadTracker() {
  const { isEnglish } = useLocale();
  const videoRef = useRef<HTMLVideoElement>(null);
  const detectorRef = useRef<FaceDetector | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRequestRef = useRef<number | null>(null);
  const busyRef = useRef(false);
  const lastFrameRef = useRef(0);
  const poseFilterRef = useRef(new AdaptivePoseFilter());
  const frameNormalizerRef = useRef(new TrackingFrameNormalizer());
  const continuityRef = useRef(new FaceTrackingContinuity());
  const runIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const progressPercentRef = useRef(-1);
  const [status, setStatus] = useState<TrackerStatus>("idle");
  const [pose, setPose] = useState<HeadPose | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState<TrackingLoadProgress | null>(null);

  const stop = useCallback(() => {
    runIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    if (frameRequestRef.current !== null) cancelAnimationFrame(frameRequestRef.current);
    frameRequestRef.current = null;
    detectorRef.current?.close();
    detectorRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    busyRef.current = false;
    lastFrameRef.current = 0;
    poseFilterRef.current.reset();
    frameNormalizerRef.current.reset();
    continuityRef.current.reset();
    progressPercentRef.current = -1;
    setPose(null);
    setLoadProgress(null);
    setStatus("idle");
  }, []);

  const start = useCallback(async () => {
    stop();
    const runId = runIdRef.current;
    const abortController = new AbortController();
    abortRef.current = abortController;
    setError(null);
    setStatus("requesting");
    const detectorTask = createDetector(abortController.signal, (progress) => {
      if (runId !== runIdRef.current) return;
      if (progress.phase !== "initializing" && progress.percent === progressPercentRef.current) return;
      progressPercentRef.current = progress.percent;
      setLoadProgress(progress);
    })
      .then((detector) => ({ detector } as const))
      .catch((detectorError: unknown) => ({ error: detectorError } as const));
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("unsupported_camera_tracking");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 15, max: 20 } },
      });
      if (runId !== runIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        void detectorTask.then((result) => "detector" in result && result.detector.close());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) throw new Error("video_element_missing");
      video.srcObject = stream;
      setStatus("loading");
      await video.play();
      const detectorResult = await detectorTask;
      if ("error" in detectorResult) throw detectorResult.error;
      const detector = detectorResult.detector;
      if (runId !== runIdRef.current) {
        detector.close();
        return;
      }
      detectorRef.current = detector;
      abortRef.current = null;
      setStatus("waiting-frame");

      let lastVideoTime = -1;
      let firstInferenceReported = false;
      const trackSettings = stream.getVideoTracks()[0]?.getSettings();
      console.info("Face tracking camera ready", {
        width: video.videoWidth,
        height: video.videoHeight,
        frameRate: trackSettings?.frameRate,
        frameCallback: "animation",
      });
      const scheduleFrame = () => {
        if (runId !== runIdRef.current) return;
        frameRequestRef.current = requestAnimationFrame(capture);
      };
      const capture = (timestamp: number) => {
        frameRequestRef.current = null;
        if (
          runId !== runIdRef.current
          || busyRef.current
          || timestamp - lastFrameRef.current < MIN_INFERENCE_INTERVAL_MS
          || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
          || video.videoWidth <= 0
          || video.videoHeight <= 0
          || video.currentTime === lastVideoTime
        ) {
          scheduleFrame();
          return;
        }
        lastFrameRef.current = timestamp;
        lastVideoTime = video.currentTime;
        busyRef.current = true;
        let keepRunning = true;
        try {
          const result = detector.detectForVideo(frameNormalizerRef.current.prepare(video), timestamp);
          const detection = result.detections.reduce<(typeof result.detections)[number] | undefined>((best, candidate) => (
            !best || (candidate.categories[0]?.score ?? 0) > (best.categories[0]?.score ?? 0) ? candidate : best
          ), undefined);
          if (!firstInferenceReported) {
            firstInferenceReported = true;
            console.info("Face tracking first frame", {
              detections: result.detections.length,
              keypoints: detection?.keypoints.length ?? 0,
            });
          }
          const markMiss = () => {
            const continuity = continuityRef.current.missed(timestamp);
            setStatus(continuity === "lost" ? "face-lost" : "recovering");
            if (continuity === "lost") {
              setPose(null);
            }
          };
          if (!detection) {
            markMiss();
          } else {
            const raw = poseFromDetection(detection.keypoints, detection.categories[0]?.score ?? 0, timestamp);
            if (!raw) {
              markMiss();
            } else {
              continuityRef.current.detected();
              const next = poseFilterRef.current.next(raw);
              setPose(next);
              setStatus("tracking");
            }
          }
        } catch (trackingError) {
          keepRunning = false;
          console.error("Face tracking frame failed", trackingError);
          setError(isEnglish ? "Face tracking stopped unexpectedly. Retry the camera or use mouse mode." : "面部追踪组件运行失败。请刷新后重试，或使用鼠标试玩。");
          setStatus("error");
          detectorRef.current = null;
          try {
            detector.close();
          } catch (closeError) {
            console.warn("Face tracking cleanup failed after inference error", closeError);
          }
          stream.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
          video.srcObject = null;
          frameNormalizerRef.current.reset();
        } finally {
          busyRef.current = false;
        }
        if (keepRunning) scheduleFrame();
      };
      scheduleFrame();
    } catch (cameraError) {
      if (runId !== runIdRef.current) return;
      console.error("Face tracking start failed", cameraError);
      setError(cameraError instanceof DOMException
        ? friendlyCameraError(cameraError, isEnglish)
        : isEnglish ? "Face tracking didn't load. Retry the camera or use mouse mode." : "面部追踪组件没有加载成功。请刷新后重试，或使用鼠标试玩。");
      setStatus("error");
      abortController.abort();
      if (abortRef.current === abortController) abortRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      void detectorTask.then((result) => "detector" in result && result.detector.close());
    }
  }, [isEnglish, stop]);

  useEffect(() => stop, [stop]);

  return { videoRef, status, pose, error, loadProgress, start, stop };
}
